// Files in a document body. Images are drawn as images, anything else as a
// chip with the name. Which one is decided by the syntax (the `!` = the
// `image` attribute), never by the file's type: the static view cannot look
// the type up, and if the syntax did not decide the form, it would change the
// moment the editor takes over from the static view.
//
// `image` and `video` (`kind=video`) together are a video player (the player
// handles its own load failures; it does not fall back to a chip).
//
// Two node views:
// - `FileView` (`trameriaFile`, a block): images of the body. An image that
//   fails twice falls back to a chip, so the chip is drawn here too.
// - `FileInlineView` (`trameriaFileInline`, inside a paragraph): a chip. Even
//   with `image` set (an image written mid-line) it stays a chip: a tall image
//   inside a line would stretch the line ("images are blocks").
//
// The chip's markup belongs to `FileChip` alone (both draw it): written twice
// it drifts, and a failing image would change shape.
//
// The size is not looked up either (the file's meta needs a request the
// static view cannot make, so the form would change at the switch). An image
// changes height once when it loads; a known limit.
//
// A failed load is retried once before falling back to the chip: right after
// a paste the file has no holder yet and others get 404 for a few seconds.
// Remembering the failure for good would leave whoever hit that window stuck.
import type { NodeViewProps } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import {
  blockIdDomAttrs,
  FILE_WIDTH_MAX,
  FILE_WIDTH_MIN,
  normalizeFileWidth,
  TrameriaFile,
  type TrameriaFileAttrs,
  TrameriaFileInline,
} from "@trameria/editor";
import { m } from "@trameria/i18n";
import {
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useMeApp } from "../../app/context";
import { copyText } from "./copy";
import { FloatingMenu, type FloatingMenuItem } from "./floating-menu";
import {
  openFileLightbox,
  VIDEO_BOX_CLASS,
  VideoPlayer,
  videoOuterStyle,
} from "./media";
import { useCoarsePointer } from "./pointer";
import { fileUrl } from "./upload";

/** Within the editor's limits (narrower, the grip covers the image; wider than the column only overflows). */
const clampWidth = (px: number) =>
  Math.min(Math.max(Math.round(px), FILE_WIDTH_MIN), FILE_WIDTH_MAX);

/**
 * An image in the body; the static view uses the same classes.
 *
 * Capped at 420px high: a 1024px square image filling the screen would hide
 * the rest of the body. `w-auto` / `object-contain` keep its ratio. The whole
 * image is seen in the borderless viewer opened from its wrapper.
 *
 * No frame (asked for: "hide the border around images"). Where it is shows by
 * the hover line on its right edge (`.trameria-file-resize`), the "…"
 * (`.trameria-file-more`) and the outline while selected.
 *
 * `pointer-events-none`: if the IMG were the mousedown target, TipTap's
 * `stopEvent` would let it through and ProseMirror would select the node. The
 * wrapping `<button>` is what is pressed.
 */
export const FILE_IMAGE_CLASS =
  "pointer-events-none block h-auto max-h-[420px] w-auto max-w-full rounded-[5px] object-contain";

/**
 * The image's wrapper: a `<button>` (not `<a>`: pressable things are buttons)
 * that opens the viewer. The static view has the same wrapper, so pressing does
 * the same there. The button's UA look (ground, padding, centring) is reset.
 */
export const FILE_IMAGE_OPEN_CLASS =
  "block w-fit max-w-full cursor-zoom-in border-0 bg-transparent p-0 text-left";

/**
 * The box around the image and its grip: the grip sits on the image's right
 * edge (`relative`), and the box is exactly the image's size (`w-fit`). The
 * static view has the same box (only the grip is missing there): with or
 * without it the body would jump at the switch.
 */
export const FILE_IMAGE_BOX_CLASS =
  "trameria-file-box relative w-fit max-w-full";

/**
 * The `<img>` style for a set width. The node view and the static view apply
 * the same values in the same order; no width, no style attribute.
 *
 * The 420px height cap is lifted: with a width chosen, cutting the height
 * would make the image narrower than set, and the grabbed edge would leave the
 * pointer. Images without a width keep the cap (it only stops unsized images
 * from filling the body).
 */
export function fileImageStyle(
  width: number | null,
): { width: string; maxHeight: string } | undefined {
  const px = normalizeFileWidth(width);
  return px === null ? undefined : { width: `${px}px`, maxHeight: "none" };
}

/** A file's chip in the body; the static view uses the same classes. */
export const FILE_CHIP_CLASS =
  "trameria-file-chip inline-flex max-w-full items-center gap-2 overflow-hidden rounded-[5px] border border-border px-2 py-1 text-[12.5px] text-fg no-underline hover:bg-hover";

/**
 * The chip's icon. One markup for the node view (React) and the static view
 * (an HTML string): a string both can take (React via
 * `dangerouslySetInnerHTML`, the static view via `innerHTML`). The color comes
 * from `currentColor`; the wrapper (`FILE_CHIP_ICON_CLASS`) passes the faint
 * text color, so dark mode needs nothing.
 */
export const FILE_CHIP_ICON_SVG =
  '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
  '<rect x="3" y="2" width="10" height="12" rx="1.5" stroke="currentColor"></rect>' +
  '<path d="M5.5 6h5M5.5 8.5h3.5" stroke="currentColor" stroke-linecap="round"></path>' +
  "</svg>";

/** Made once: React 19 compares by identity and re-sets innerHTML when it differs. */
const FILE_CHIP_ICON_HTML = { __html: FILE_CHIP_ICON_SVG };

/** The icon's wrapper: faint, takes no hits (an SVG target would let PM select the node). */
export const FILE_CHIP_ICON_CLASS = "pointer-events-none flex-none text-faint";

/** How long a failed image waits before its one retry (for its holder to be set). */
export const FILE_IMAGE_RETRY_MS = 3000;

/**
 * The name chip: the only markup shared by the block view (an image's
 * fallback) and the inline view. The static view builds the same DOM.
 *
 * It is an `<a>` (`?dl=1` saves it). No "…": hover controls over a small
 * inline box hide the name. A block chip is removed through the block menu,
 * an inline one by editing the text.
 */
function FileChip({
  href,
  name,
  selected,
}: {
  href: string;
  name: string;
  /** ProseMirror has a NodeSelection on it (look in the CSS). */
  selected: boolean;
}) {
  return (
    <a
      href={href}
      className={FILE_CHIP_CLASS}
      data-selected={selected ? "true" : undefined}
      // Keeps the body's selection and focus.
      onMouseDown={(e) => e.preventDefault()}
    >
      <span
        className={FILE_CHIP_ICON_CLASS}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: fixed markup shared with the static view (FILE_CHIP_ICON_SVG); nothing from outside goes in
        dangerouslySetInnerHTML={FILE_CHIP_ICON_HTML}
      />
      <span className="truncate">{name}</span>
    </a>
  );
}

/**
 * How the image is loading, always paired with its `fileId`.
 *
 * `@tiptap/react`'s `ReactNodeView.update()` swaps props for a node of the same
 * type without comparing attributes, and `@tiptap/y-tiptap` replaces the whole
 * document on remote changes - so one node view instance can turn into another
 * file (e.g. when someone removes the file above). Kept apart from `fileId`, a
 * readable image would become a chip from the previous file's failure, with
 * its one retry already spent.
 */
interface ImageLoad {
  fileId: string;
  /** 0: the first load, 1: the retry. */
  attempt: number;
  /** Both failed (fall back to the chip). */
  gaveUp: boolean;
}

function freshLoad(fileId: string): ImageLoad {
  return { fileId, attempt: 0, gaveUp: false };
}

/** The width while dragging, also paired with `fileId` (same reason as ImageLoad). */
interface WidthDrag {
  fileId: string;
  pointerId: number;
  startX: number;
  /** The measured width when grabbed (images without `?w=` need a start too). */
  startWidth: number;
  /** The width shown now (written once on release). */
  width: number;
  /** Moved at all: a press alone writes nothing (no undo step). */
  moved: boolean;
}

export function FileView({
  node,
  editor,
  selected,
  updateAttributes,
  deleteNode,
}: NodeViewProps) {
  const { fileId, name, image, width, video } = node.attrs as TrameriaFileAttrs;
  // Node views render through portals of EditorContent's tree, so context reaches them.
  const { apiBase } = useMeApp();
  const src = fileUrl(apiBase, fileId);
  const download = fileUrl(apiBase, fileId, { download: true });
  const [saved, setLoad] = useState<ImageLoad>(() => freshLoad(fileId));
  // Turned into another file: start over. Compared while rendering, not
  // chased with an effect (no props -> state copies).
  const load = saved.fileId === fileId ? saved : freshLoad(fileId);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The "…" menu's anchor (the rectangle when opened), null when closed. */
  const [menu, setMenu] = useState<DOMRect | null>(null);
  /** The image's wrapper: the viewer opened from "…" returns its focus there. */
  const openButton = useRef<HTMLButtonElement>(null);
  /**
   * On touch devices, the first tap shows the "…", the second opens the image
   * (asked for: "one tap shows the dots, another tap enlarges"). Showing "…"
   * all the time put a circle on every image. Mice do not use this (hover
   * shows it, one click opens).
   */
  const coarse = useCoarsePointer();
  const [armed, setArmed] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  // frontend-development: not a fetch; a tap outside the image hides its "…" again.
  useEffect(() => {
    if (!armed) return;
    const onDown = (e: PointerEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setArmed(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [armed]);
  // frontend-development: not a fetch; clears the retry timer when it goes away.
  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  // --- dragging the width ---
  //
  // Nothing goes to Yjs while dragging: writing on every move would make
  // everyone else's screen shake. Only this view's state shows it, and one
  // `updateAttributes` on release writes it.
  // What is measured: the `<img>`, or the box for a video.
  const mediaRef = useRef<HTMLElement | null>(null);
  const drag = useRef<WidthDrag | null>(null);
  const [preview, setPreview] = useState<{
    fileId: string;
    width: number;
  } | null>(null);
  const attrWidth = normalizeFileWidth(width);
  const shownWidth =
    preview !== null && preview.fileId === fileId ? preview.width : attrWidth;

  const startResize = (e: ReactPointerEvent<HTMLButtonElement>) => {
    // Every press starts from the measured width (not the stored `?w=`):
    // images without one need a start, and an image shrunk by `max-w-full`
    // keeps the grabbed edge under the pointer (as table columns do).
    let measured = mediaRef.current?.getBoundingClientRect().width ?? 0;
    // A video without a width starts from the video's visible width, not the
    // box (the full column): the frame is capped at 420px high with black
    // bars, and starting from the box would lift the cap on the first move and
    // blow a portrait video up at once.
    if (video && attrWidth === null) {
      const frame = boxRef.current?.querySelector("[data-video-player]");
      const el = boxRef.current?.querySelector("video");
      const frameH = frame?.getBoundingClientRect().height ?? 0;
      if (el && el.videoWidth > 0 && el.videoHeight > 0 && frameH > 0) {
        measured = Math.min(
          measured,
          (frameH * el.videoWidth) / el.videoHeight,
        );
      }
    }
    const start = clampWidth(measured > 0 ? measured : (attrWidth ?? 0));
    drag.current = {
      fileId,
      pointerId: e.pointerId,
      startX: e.clientX,
      startWidth: start,
      width: start,
      moved: false,
    };
    // Not in happy-dom. In browsers, events keep coming outside the button.
    if (typeof e.currentTarget.setPointerCapture === "function") {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    setPreview({ fileId, width: start });
  };
  const moveResize = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    if (e.clientX !== d.startX) d.moved = true;
    d.width = clampWidth(d.startWidth + (e.clientX - d.startX));
    setPreview({ fileId: d.fileId, width: d.width });
  };
  const endResize = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    drag.current = null;
    setPreview(null);
    // A press, or a drag back to the same width: no write, no undo step.
    if (!d.moved || d.width === attrWidth) return;
    updateAttributes({ width: d.width });
  };
  const cancelResize = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    drag.current = null;
    setPreview(null);
  };

  // The grip: the image's right edge, full height (what can be grabbed spans
  // the whole edge, as table borders do). Look and "not on touch devices" are
  // in the CSS (`.trameria-file-resize`). A `<button>`: TipTap's `stopEvent`
  // keeps BUTTON mousedowns from ProseMirror; a DIV would let PM select the
  // node and take the focus.
  const grip = (
    <button
      type="button"
      // Pointer only (widths are not changed from the keyboard), like table grips.
      tabIndex={-1}
      aria-label={m.blocks_file_resize()}
      className="trameria-file-resize"
      data-active={preview !== null ? "true" : undefined}
      onMouseDown={(e) => e.preventDefault()}
      onPointerDown={startResize}
      onPointerMove={moveResize}
      onPointerUp={endResize}
      onPointerCancel={cancelResize}
      // Capture taken away: cancel. A normal release ends here too, after
      // pointerup cleared `drag.current`.
      onLostPointerCapture={cancelResize}
      // Back to the default width (drop `?w=`), as table grips do.
      onDoubleClick={() => {
        drag.current = null;
        setPreview(null);
        if (attrWidth !== null) updateAttributes({ width: null });
      }}
    />
  );

  // --- the "…" shown on hover ---
  //
  // When it shows (hover, menu open, selected, armed on touch) and its look
  // are in the CSS (`.trameria-file-more`). Not on chips (it would cover the
  // name) and not in the static view (nothing to edit there).
  /** Returns the focus to the body before closing. */
  const closeMenu = () => {
    setMenu(null);
    editor.commands.focus();
  };
  const menuItems: FloatingMenuItem[] = [
    // A video's full screen is its "enlarge".
    ...(video
      ? []
      : [
          {
            label: m.blocks_file_enlarge(),
            onSelect: () => {
              const from = openButton.current;
              closeMenu();
              // Opened from the image's button, so closing returns there.
              if (from) openFileLightbox(from, { src, name, download });
            },
          },
        ]),
    {
      label: m.blocks_file_download(),
      onSelect: () => {
        closeMenu();
        // The chip's URL; the answer is an attachment, so the page stays.
        window.location.assign(download);
      },
    },
    {
      label: m.blocks_file_copy_link(),
      onSelect: () => {
        closeMenu();
        // An absolute URL: a relative one does not open where it is pasted.
        void copyText(new URL(src, window.location.href).href);
      },
    },
    // Resetting is also a double click on the grip, but only people who know
    // the grip see that. Offered only for images with a width.
    ...(attrWidth !== null
      ? [
          {
            label: m.blocks_file_reset_width(),
            onSelect: () => {
              closeMenu();
              updateAttributes({ width: null });
            },
          },
        ]
      : []),
    {
      label: m.blocks_file_delete(),
      danger: true,
      onSelect: () => {
        // ProseMirror keeps the focus after removing (returning it to the body
        // first would point at what is about to go).
        setMenu(null);
        deleteNode();
      },
    },
  ];

  const more = (
    <>
      <button
        type="button"
        // Not in the Tab order (parts in a document do not split the body's order).
        tabIndex={-1}
        aria-label={m.blocks_file_actions()}
        aria-haspopup="menu"
        aria-expanded={menu !== null}
        className="trameria-file-more"
        data-open={menu !== null ? "true" : undefined}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => setMenu(e.currentTarget.getBoundingClientRect())}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 16 16"
          aria-hidden="true"
          className="pointer-events-none"
        >
          <circle cx="3.5" cy="8" r="1.4" fill="currentColor" />
          <circle cx="8" cy="8" r="1.4" fill="currentColor" />
          <circle cx="12.5" cy="8" r="1.4" fill="currentColor" />
        </svg>
      </button>
      {menu && (
        <FloatingMenu
          anchor={menu}
          placement="below"
          label={m.blocks_file_actions()}
          items={menuItems}
          onClose={closeMenu}
        />
      )}
    </>
  );

  const body =
    image && video ? (
      // A video. Its box is the image's (`.trameria-file-box`), so the "…"
      // and grip CSS applies as is. The width goes on the box; the player
      // fills it. `key={fileId}`: a view that turned into another file does
      // not carry over the previous one's position or failure.
      <div
        ref={(el) => {
          boxRef.current = el;
          mediaRef.current = el;
        }}
        className={VIDEO_BOX_CLASS}
        style={videoOuterStyle(shownWidth)}
        data-selected={selected ? "true" : undefined}
        // On touch devices the "…" always shows (the CSS hides it unless
        // armed): pressing a video opens its viewer, so no first-tap step.
        data-armed={coarse ? "true" : undefined}
      >
        <VideoPlayer
          key={fileId}
          src={src}
          name={name}
          download={download}
          width={shownWidth}
        />
        {more}
        {grip}
      </div>
    ) : image && !load.gaveUp ? (
      // The height is capped, so the borderless viewer shows the whole image
      // (no page change).
      //
      // The node can still be selected: `preventDefault` stops only presses on
      // the image itself; pressing the box's margin (images are at most 420px
      // high, the column 696px wide) lets ProseMirror select the node, and the
      // drag handle and block menu work as usual.
      //
      // The grip and "…" sit outside the wrapping button (a button inside a
      // button is invalid DOM).
      <div
        ref={boxRef}
        className={FILE_IMAGE_BOX_CLASS}
        data-selected={selected ? "true" : undefined}
        data-armed={armed ? "true" : undefined}
      >
        <button
          type="button"
          ref={openButton}
          // The static view picks this mark up by delegation; both have it.
          data-file-open=""
          tabIndex={-1}
          aria-label={m.blocks_file_enlarge_named({ name })}
          className={FILE_IMAGE_OPEN_CLASS}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            // Touch: the first tap shows "…", the second opens. A body that
            // cannot be written (reading on a phone) needs no "…", so it opens
            // at once (it looked like "pressing does nothing" on an iPhone).
            if (coarse && !armed && editor.isEditable) {
              setArmed(true);
              return;
            }
            openFileLightbox(e.currentTarget, { src, name, download });
          }}
        >
          <img
            // A new element per attempt: assigning the same src again does not reload.
            // `fileId` too, so a view that turned into another file does not reuse it.
            key={`${fileId}:${load.attempt}`}
            ref={(el) => {
              mediaRef.current = el;
            }}
            src={src}
            alt={name}
            className={FILE_IMAGE_CLASS}
            // While dragging only this style changes (the document is not written).
            style={fileImageStyle(shownWidth)}
            onError={() => {
              if (timer.current) clearTimeout(timer.current);
              if (load.attempt > 0) {
                setLoad({ ...load, gaveUp: true });
                return;
              }
              timer.current = setTimeout(
                () => setLoad({ fileId, attempt: 1, gaveUp: false }),
                FILE_IMAGE_RETRY_MS,
              );
            }}
          />
        </button>
        {more}
        {grip}
      </div>
    ) : (
      <FileChip href={download} name={name} selected={selected} />
    );

  return (
    // A node view's DOM does not go through renderHTML, so the block id is set
    // here (`blockIdDomAttrs` owns the attribute name and the "none until
    // assigned" rule); the block's hit areas and drags look for it.
    <NodeViewWrapper data-trameria-file={fileId} {...blockIdDomAttrs(node)}>
      {body}
    </NodeViewWrapper>
  );
}

/**
 * Events inside a video player are not given to ProseMirror (dragging the seek
 * bar or the volume, keys, clicks on the frame): given to it, they would select
 * the node or type a space into the body. Drags and drops still go to it
 * (moving blocks is the editor's job).
 *
 * Images do not use this: they rely on TipTap's default (stopping only on the
 * `<button>`), so pressing the box's margin can select them.
 */
export function videoStopEvent({ event }: { event: Event }): boolean {
  if (event.type.startsWith("drag") || event.type === "drop") return false;
  // Outside the box (the node view's margin) goes through, as with images.
  const target = event.target;
  return (
    target instanceof Element && target.closest(".trameria-file-box") !== null
  );
}

const isVideoNode = (node: PMNode) =>
  Boolean(node.attrs.image && node.attrs.video);

/**
 * A node that turned from image to video or back gets a new node view
 * (false): `@tiptap/react` reuses the view for the same node type without
 * comparing attributes, which would keep the other `stopEvent`.
 */
export function fileNodeViewUpdate({
  oldNode,
  newNode,
  updateProps,
}: {
  oldNode: PMNode;
  newNode: PMNode;
  updateProps: () => void;
}): boolean {
  if (isVideoNode(oldNode) !== isVideoNode(newNode)) return false;
  // The same node: nothing to do (as TipTap's default; skips a render).
  if (oldNode === newNode) return true;
  updateProps();
  return true;
}

/** The editor's block file with this node view (same name, so it replaces the built-in). */
export const FileWithView = TrameriaFile.extend({
  addNodeView() {
    const imageView = ReactNodeViewRenderer(FileView, {
      update: fileNodeViewUpdate,
    });
    const videoView = ReactNodeViewRenderer(FileView, {
      update: fileNodeViewUpdate,
      stopEvent: videoStopEvent,
    });
    return (props) =>
      isVideoNode(props.node) ? videoView(props) : imageView(props);
  },
});

/**
 * A file inside a paragraph: a chip only ("images are blocks, but files are
 * inline - I want several on one line"). Even with `image` (an image written
 * mid-paragraph) it is a chip.
 *
 * No block id: inline nodes have none (not in `BLOCK_ID_TYPES`); the
 * paragraph holding the chip is the block.
 */
export function FileInlineView({ node, selected }: NodeViewProps) {
  const { fileId, name } = node.attrs as TrameriaFileAttrs;
  const { apiBase } = useMeApp();
  return (
    // Inside a paragraph, so a `<span>` (a div cannot sit in a `<p>`). The
    // mark is the static view's `data-trameria-file-inline`.
    <NodeViewWrapper as="span" data-trameria-file-inline={fileId}>
      <FileChip
        href={fileUrl(apiBase, fileId, { download: true })}
        name={name}
        selected={selected}
      />
    </NodeViewWrapper>
  );
}

/** The editor's inline file with this node view. */
export const FileInlineWithView = TrameriaFileInline.extend({
  addNodeView() {
    return ReactNodeViewRenderer(FileInlineView);
  },
});
